import Cosmic from 'cosmicjs';

import { BLOCK_TYPES, IPage, IPageBlock, IPageFetcher } from '../page';

export type ICosmicConfig = {
    bucket: string;
    readKey?: string;
};

export interface ICosmicImage {
    url: string;
    imgix_url: string;
}

export interface ICosmicImageMetadata {
    image: ICosmicImage;
    mobile_image: ICosmicImage;
    image_alt_text: string;
    title: string;
    subtitle: string;
    cta_text: string;
    cta_url: string;
}

type ICosmicEntity<TMetadata, TType extends string> = {
    id: string;
    order?: number;
    slug?: string;
    title?: string;
    content?: string;
    bucket?: string;
    created_at?: string;
    created_by?: string;
    modified_at?: string;
    created?: string;
    status?: string;
    thumbnail?: string;
    modified_by?: string;
    publish_at?: any;
    published_at: string;
    unpublish_at?: any;
    type: TType;
    metadata: TMetadata;
};

export type ICosmicFeaturedAd = ICosmicEntity<ICosmicImageMetadata, 'featured-ad'>;

type ICosmicHDYHAUAnswer = {
    value: string;
};

type ICosmicHDYHAU = ICosmicEntity<{
    question: string;
    answers: ICosmicHDYHAUAnswer[];
}, 'how-did-you-hear-about-us'>;

type ICosmicFeature = ICosmicFeaturedAd | ICosmicHDYHAU;

type ICosmicPageMetadata = {
    features?: ICosmicFeature[]
};

export type ICosmicPage = {
    object: ICosmicEntity<ICosmicPageMetadata, 'pages'>;
};

function shuffle (array: string[]): string[] {
    let currentIndex: number = array.length;

    while (currentIndex !== 0) {
        const randomIndex: number = Math.floor(Math.random() * currentIndex);

        currentIndex = currentIndex - 1;

        [array[currentIndex], array[randomIndex]] = [array[randomIndex], array[currentIndex]];
    }

    return array;
}

export class CosmicPageClient implements IPageFetcher {
    private bucket: Cosmic.Bucket;

    public constructor (cosmicConfig: ICosmicConfig) {
        this.bucket = Cosmic().bucket({
            slug: cosmicConfig.bucket,
        });
    }

    public async fetch (id: string): Promise<IPage> {
        try {
            const data: ICosmicPage = await this.bucket.getObject<ICosmicPage>({
                id,
                props: 'slug,title,metadata',
            });

            return CosmicPageClient.transformPage(data);
        } catch (error) {
            throw error;
        }
    }

    private static getOptimizedImage (image: ICosmicImage): string {
        const url: URL = new URL(image.imgix_url);
        const params = new URLSearchParams(url.search);
        const auto: string | null = params.get('auto');
        const fm: string | null = params.get('fm');

        if (!auto) {
            params.append('auto', 'format');
        }

        if (!fm) {
            params.append('fm', 'jpg');
        }

        return `${url.origin}${url.pathname}?${params.toString()}`;
    }

    private static transformFeaturedAd (featureAd: ICosmicFeaturedAd): IPageBlock {
        const { cta_url, title, subtitle, cta_text, image, mobile_image, image_alt_text } = featureAd.metadata;
        const { published_at } = featureAd;

        return {
            type: BLOCK_TYPES.FEATURED_AD,
            content: {
                link: cta_url,
                title,
                subtitle,
                cta: cta_text,
                publishedAt: new Date(published_at),
                image: {
                    src: image.url,
                    alt: image_alt_text,
                    sizes: {
                        desktop: CosmicPageClient.getOptimizedImage(image),
                        mobile: CosmicPageClient.getOptimizedImage(mobile_image),
                    },
                },
            },
        };
    }

    private static randomizeHDYHAUAnswers (answers: string[]): string[] {
        const otherIndex: number = answers.indexOf('Other');
        const randomAnswers: string[] = [...answers];

        if (otherIndex > -1) {
            randomAnswers.splice(otherIndex, 1);
        }

        return [...shuffle(randomAnswers), 'Other'];
    }

    private static transformHDYHAU (hdyhau: ICosmicHDYHAU): IPageBlock {
        const { question, answers } = hdyhau.metadata;

        return {
            type: BLOCK_TYPES.HDYHAU,
            content: {
                question,
                answers: CosmicPageClient.randomizeHDYHAUAnswers(answers.map((answer: ICosmicHDYHAUAnswer): string => {
                    return answer.value;
                })),
            },
        };
    }

    private static transformPage (data: ICosmicPage): IPage  {
        const blocks: IPageBlock[] | undefined = data.object.metadata.features?.map((feature: ICosmicFeature): IPageBlock => {
            if (feature.type === 'featured-ad') {
                return CosmicPageClient.transformFeaturedAd(feature);
            }

            return CosmicPageClient.transformHDYHAU(feature);
        });

        if (!blocks) {
            return {
                blocks: [],
            };
        }

        return {
            blocks,
        };
    }
}
